"use client"
import { useState } from "react"
import { motion } from "motion/react"
import { ScrollReveal } from "@/components/scroll/ScrollReveal"
import Link from "next/link"
import { Footer } from "@/components/layout/Footer"
import { Breadcrumbs, KeyTakeaways, FaqSection } from "@/components/seo/AeoBlocks"
import { HeroRings, DotGrid, FloatingDots, ScanLine, CornerAccents } from "@/components/ui/BackgroundDecor"
import type { ServicesContent, ServiceItem } from "./services-content"

const BG = "#0B0F1C"
const ACCENT = "#C4541A"
const MUTED = "rgba(255,255,255,0.6)"
const SURFACE = "rgba(255,255,255,0.05)"
const BORDER = "rgba(255,255,255,0.08)"

function Eyebrow({ label }: { label: string }) {
  return (
    <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 32 }}>
      <motion.span
        initial={{ width: 0 }}
        whileInView={{ width: 32 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: [0.33, 1, 0.68, 1] }}
        style={{ height: 1, background: ACCENT, flexShrink: 0, display: "block" }}
      />
      <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: MUTED }}>{label}</span>
    </div>
  )
}

export function ServicesClient({ content }: { content: ServicesContent }) {
  const [openFaq, setOpenFaq] = useState<number | null>(null)
  const { services, faqs } = content
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  }

  return (
    <main style={{ background: BG, color: "#fff", minHeight: "100vh" }}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      {/* HERO */}
      <div style={{ position: "relative", overflow: "hidden" }}>
        <motion.div
          animate={{ opacity: [0.7, 1, 0.7], scale: [1, 1.08, 1] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          style={{ position: "absolute", inset: 0, pointerEvents: "none", background: `radial-gradient(ellipse 80% 65% at 85% 25%, rgba(196,84,26,0.40) 0%, transparent 66%)` }}
        />
        <HeroRings side="right" />
        <DotGrid />
        <FloatingDots />
        <ScanLine />
        <CornerAccents />
        <section style={{ padding: "clamp(100px, 18vw, 160px) clamp(20px, 4vw, 48px) 60px", maxWidth: 1400, margin: "0 auto", position: "relative", zIndex: 1 }}>
          <ScrollReveal effect="fade-up">
            <Eyebrow label={content.heroEyebrow} />
            <h1 style={{ fontSize: "clamp(48px, 8vw, 120px)", fontWeight: 900, letterSpacing: "-0.04em", lineHeight: 0.88, marginBottom: 40 }}>
              {content.heroHeadingLine1}<br /><span style={{ color: ACCENT }}>{content.heroHeadingLine2}</span>
            </h1>
            <p style={{ fontSize: "clamp(16px, 1.4vw, 20px)", color: MUTED, maxWidth: 560, lineHeight: 1.65 }}>
              {content.heroIntro}
            </p>
          </ScrollReveal>
        </section>
      </div>

      {/* SERVICE CARDS */}
      <section style={{ padding: "0 clamp(20px, 4vw, 48px)", maxWidth: 1400, margin: "0 auto" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {services.map((s, i) => (
            <ScrollReveal key={s.number} effect="fade-up" delay={i * 0.08}>
              <ServiceRow service={s} index={i} />
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* INCLUDED IN EVERY PROJECT */}
      <section style={{ padding: "clamp(60px, 8vw, 100px) clamp(20px, 4vw, 48px)", maxWidth: 1400, margin: "0 auto" }}>
        <ScrollReveal effect="fade-up">
          <motion.div
            animate={{ boxShadow: ["0 0 0px rgba(196,84,26,0)", "0 0 40px rgba(196,84,26,0.22)", "0 0 0px rgba(196,84,26,0)"] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            style={{ padding: "clamp(32px, 4vw, 64px)", border: `1px solid rgba(196,84,26,0.20)`, borderRadius: 24, background: `linear-gradient(135deg, rgba(196,84,26,0.08) 0%, ${SURFACE} 60%)` }}
          >
            <Eyebrow label={content.includedEyebrow} />
            <h2 style={{ fontSize: "clamp(28px, 3vw, 48px)", fontWeight: 900, letterSpacing: "-0.04em", lineHeight: 1, marginBottom: 48 }}>
              {content.includedHeadingLine1}<br /><span style={{ color: ACCENT }}>{content.includedHeadingLine2}</span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3" style={{ gap: 32 }}>
              {content.includedItems.map((item, i) => (
                <ScrollReveal key={item.title} effect="fade-up" delay={i * 0.06}>
                  <motion.div
                    whileHover={{ y: -4 }}
                    transition={{ type: "spring", stiffness: 350, damping: 28 }}
                    style={{ cursor: "default" }}
                  >
                    <motion.div
                      whileHover={{ scale: 1.15 }}
                      transition={{ type: "spring", stiffness: 400, damping: 20 }}
                      style={{ fontSize: 28, marginBottom: 12, display: "inline-block" }}
                    >{item.icon}</motion.div>
                    <p style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>{item.title}</p>
                    <p style={{ color: MUTED, fontSize: 14, lineHeight: 1.6 }}>{item.desc}</p>
                  </motion.div>
                </ScrollReveal>
              ))}
            </div>
          </motion.div>
        </ScrollReveal>
      </section>

      {/* FAQ */}
      <section style={{ padding: "0 clamp(20px, 4vw, 48px) clamp(60px, 8vw, 100px)", maxWidth: 800, margin: "0 auto" }}>
        <ScrollReveal effect="fade-up">
          <Eyebrow label={content.faqEyebrow} />
          <h2 style={{ fontSize: "clamp(28px, 3vw, 48px)", fontWeight: 900, letterSpacing: "-0.04em", marginBottom: 48 }}>
            {content.faqHeadingLine1}<br /><span style={{ color: ACCENT }}>{content.faqHeadingLine2}</span>
          </h2>
        </ScrollReveal>
        <div style={{ display: "flex", flexDirection: "column" }}>
          {faqs.map((faq, i) => (
            <ScrollReveal key={i} effect="fade-up" delay={i * 0.05}>
              <div style={{ borderBottom: `1px solid ${BORDER}` }}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  style={{ width: "100%", textAlign: "left", padding: "24px 0", background: "none", border: "none", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, fontFamily: "inherit" }}
                >
                  <span style={{ fontSize: "clamp(16px, 1.2vw, 18px)", fontWeight: 600, color: "#fff" }}>{faq.q}</span>
                  <motion.span
                    animate={{ rotate: openFaq === i ? 45 : 0 }}
                    style={{ fontSize: 24, color: ACCENT, flexShrink: 0, display: "block", lineHeight: 1 }}
                  >
                    +
                  </motion.span>
                </button>
                <motion.div
                  initial={false}
                  animate={{ height: openFaq === i ? "auto" : 0, opacity: openFaq === i ? 1 : 0 }}
                  style={{ overflow: "hidden" }}
                >
                  <p style={{ color: MUTED, fontSize: 16, lineHeight: 1.7, paddingBottom: 24 }}>{faq.a}</p>
                </motion.div>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <div style={{ borderTop: `1px solid ${BORDER}`, padding: "clamp(60px, 8vw, 100px) clamp(20px, 4vw, 48px)" }}>
        <ScrollReveal effect="scale">
          <div style={{ maxWidth: 1400, margin: "0 auto", textAlign: "center" }}>
            <h2 style={{ fontSize: "clamp(36px, 5vw, 80px)", fontWeight: 900, letterSpacing: "-0.04em", lineHeight: 0.92, marginBottom: 32 }}>
              {content.cta.headingLine1}<br /><span style={{ color: ACCENT }}>{content.cta.headingLine2}</span>
            </h2>
            <p style={{ color: MUTED, fontSize: 18, maxWidth: 480, margin: "0 auto 48px", lineHeight: 1.65 }}>
              {content.cta.body}
            </p>
            <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
              <Link href={content.cta.primaryHref}>
                <motion.div whileHover={{ scale: 1.04, background: "#D4601F" }} whileTap={{ scale: 0.97 }} style={{ padding: "18px 48px", background: ACCENT, color: "#fff", borderRadius: 100, fontWeight: 700, fontSize: 16, cursor: "pointer" }}>
                  {content.cta.primaryLabel}
                </motion.div>
              </Link>
              <Link href={content.cta.secondaryHref}>
                <motion.div whileHover={{ borderColor: "rgba(255,255,255,0.3)" }} style={{ padding: "18px 48px", border: `1px solid ${BORDER}`, color: "#fff", borderRadius: 100, fontWeight: 600, fontSize: 16, cursor: "pointer" }}>
                  {content.cta.secondaryLabel}
                </motion.div>
              </Link>
            </div>
          </div>
        </ScrollReveal>
      </div>

      {/* AEO/GEO structure — see components/seo/AeoBlocks.tsx for why each block exists. */}
      <section style={{ padding: "0 clamp(20px, 4vw, 48px) 40px", maxWidth: 1400, margin: "0 auto" }}>
        <Breadcrumbs trail={[{ name: "Services", path: "/services" }]} />
        <KeyTakeaways items={content.takeaways} />
        <FaqSection faqs={content.aeoFaqs} />
      </section>

      <Footer />
    </main>
  )
}

function ServiceRow({ service, index }: { service: ServiceItem; index: number }) {
  const [open, setOpen] = useState(false)
  return (
    <div style={{ border: `1px solid ${BORDER}`, borderRadius: 20, overflow: "hidden", marginBottom: 12 }}>
      <button
        onClick={() => setOpen(!open)}
        style={{ width: "100%", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", padding: "clamp(28px, 3vw, 44px) clamp(24px, 3vw, 40px)" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 24 }}>
          <div style={{ textAlign: "left" }}>
            <p style={{ fontSize: 11, letterSpacing: "0.15em", color: MUTED, textTransform: "uppercase", marginBottom: 8 }}>{service.number}</p>
            <h2 style={{ fontSize: "clamp(24px, 4vw, 56px)", fontWeight: 900, letterSpacing: "-0.04em", color: "#fff", margin: 0 }}>{service.title}</h2>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
            <p className="hidden md:block" style={{ color: MUTED, fontSize: 16, maxWidth: 320, textAlign: "right" }}>{service.tagline}</p>
            <motion.div
              animate={{ rotate: open ? 45 : 0 }}
              style={{ width: 48, height: 48, borderRadius: "50%", border: `1px solid ${BORDER}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, color: ACCENT, flexShrink: 0 }}
            >
              +
            </motion.div>
          </div>
        </div>
      </button>

      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0 }}
        style={{ overflow: "hidden" }}
      >
        <div
          className="grid grid-cols-1 md:grid-cols-2"
          style={{ gap: 48, padding: "clamp(24px, 3vw, 40px) clamp(24px, 3vw, 40px) clamp(28px, 3vw, 44px)", borderTop: `1px solid ${BORDER}` }}
        >
          <div>
            <p style={{ color: MUTED, fontSize: 16, lineHeight: 1.75, marginBottom: 32 }}>{service.description}</p>
            <p style={{ fontSize: 13, fontWeight: 700, color: ACCENT, fontStyle: "italic" }}>&ldquo;{service.outcome}&rdquo;</p>
          </div>
          <div>
            <p style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: MUTED, marginBottom: 20 }}>Deliverables</p>
            <ul style={{ listStyle: "none", padding: 0, display: "flex", flexDirection: "column", gap: 12, marginBottom: 32 }}>
              {service.deliverables.map(d => (
                <li key={d} style={{ display: "flex", gap: 12, alignItems: "flex-start", color: "#fff", fontSize: 15 }}>
                  <span style={{ color: ACCENT, marginTop: 2, flexShrink: 0 }}>✦</span>{d}
                </li>
              ))}
            </ul>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 32 }}>
              {service.tags.map(t => (
                <span key={t} style={{ padding: "4px 14px", borderRadius: 100, border: `1px solid ${BORDER}`, fontSize: 12, color: MUTED }}>{t}</span>
              ))}
            </div>
            {/* A reader on a package had nowhere to go but the next package. Each one now
                links to the case study that proves it. */}
            <p style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: MUTED, marginBottom: 12 }}>Proof</p>
            <ul style={{ listStyle: "none", padding: 0, display: "flex", flexDirection: "column", gap: 10 }}>
              {service.proof.map(pr => (
                <li key={pr.href}>
                  <Link href={pr.href} style={{ color: "#fff", fontSize: 14, textDecoration: "underline", textUnderlineOffset: 4, textDecorationColor: "rgba(255,255,255,0.3)" }}>{pr.label} →</Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </motion.div>
    </div>
  )
}
